<template>
  <div class="flex h-screen flex-col overflow-hidden bg-primary text-text-main font-sans">
    <!-- Demo-Disclaimer-Banner -->
    <div
      class="flex shrink-0 items-center justify-center gap-2 bg-accent px-4 py-2 text-center text-sm font-semibold text-white"
    >
      <MdiIcon
        :path="mdiInformationOutline"
        size="20"
      />
      <span>
        Demo-Modus: Du erkundest CodeLab ohne Anmeldung.
        Änderungen werden <strong>nicht gespeichert</strong>.
      </span>
    </div>

    <!-- IDE-Layout (ohne Login, ohne Speichern) -->
    <div
      class="grid min-h-0 flex-1 grid-rows-[64px_minmax(0,1fr)] overflow-hidden transition-all duration-300 ease-in-out"
      :class="gridColsClass"
      :style="{ '--rp-width': panelWidth + 'px' }"
    >
      <TopBar
        v-model:run-state="runState"
        v-model:speed-val="speedVal"
        :theme="theme"
        :is-fullscreen="isFullscreen"
        :is-debugging="isDebugging"
        @toggle-theme="toggleTheme"
        @toggle-fullscreen="toggleFullscreen"
        @toggle-debug-panel="isDebugging = $event"
        @undo="handleIdeAction('keyboard.undo')"
        @redo="handleIdeAction('keyboard.redo')"
        @reset="notifyDisabled('Zum Zurücksetzen des Codes lade einfach die Seite neu (F5).')"
        @step-into="handleIdeAction('interpreter.stepInto')"
        @step-over="handleIdeAction('interpreter.stepOver')"
        @step-out="handleIdeAction('interpreter.stepOut')"
        @save="notifyDisabled('Speichern ist im Demo-Modus deaktiviert – deine Änderungen bleiben nur bis zum Neuladen der Seite erhalten.')"
        @open-profile="isProfileOpen = true"
      />
      <LeftSidebar
        v-model:active-file="activeFile"
        :root="sidebarRoot"
        username="Demo"
        :demo-mode="true"
        :class="{ 'w-0 overflow-hidden border-r-0 p-0': !showLeftSidebar }"
        class="transition-all duration-300 ease-in-out"
        @open-new-file="notifyDisabled('Neue Dateien können im Demo-Modus nicht angelegt werden.')"
        @rename-file="notifyDisabled('Dateien umbenennen ist im Demo-Modus deaktiviert.')"
        @delete-file="notifyDisabled('Dateien löschen ist im Demo-Modus deaktiviert.')"
        @move-file="notifyDisabled('Dateien verschieben ist im Demo-Modus deaktiviert.')"
        @change-version="notifyDisabled('Der Versionswechsel ist im Demo-Modus nicht verfügbar.')"
        @view-history="notifyDisabled('Die Versionshistorie ist im Demo-Modus nicht verfügbar.')"
        @change-permissions="notifyDisabled('Berechtigungen können im Demo-Modus nicht geändert werden.')"
        @show-submissions="notifyDisabled('Abgaben sind im Demo-Modus nicht verfügbar.')"
      />
      <EditorArea
        ref="editorAreaRef"
        v-model:run-state="runState"
        v-model:speed-val="speedVal"
        :active-file="activeFilePath"
        :files="ideFiles"
        :config="ideConfig"
        :font-size="fontSize"
        :theme="theme"
        :is-debugging="isDebugging"
        :show-left-sidebar="showLeftSidebar"
        :show-right-sidebar="showRightSidebar"
        :show-bottom-panel="showBottomPanel"
        :disable-feedback="true"
        @change="onIdeChange"
        @feedback-disabled="notifyDisabled('Feedback ist in der Demo-Version gesperrt. Melde dich an, um Feedback zu geben.')"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
        @toggle-left-sidebar="toggleLeftSidebar"
        @toggle-right-sidebar="toggleRightSidebar"
        @toggle-bottom-panel="toggleBottomPanel"
        @toggle-debug-panel="isDebugging = $event"
        @toggle-fullscreen="toggleFullscreen"
      />
      <RightPanel
        :class="{ 'w-0 overflow-hidden border-l-0 p-0': !showRightSidebar }"
        class="transition-all duration-300 ease-in-out"
        @resize="panelWidth = $event"
      />
    </div>

    <!-- Kurze, aktionsspezifische Info-Meldung für deaktivierte Buttons -->
    <Transition
      enter-active-class="transition-opacity duration-200 ease-in-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-200 ease-in-out"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="noticeMessage"
        class="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-standard border border-tertiary bg-secondary px-5 py-3 text-sm font-medium text-text-main shadow-lg"
      >
        {{ noticeMessage }}
      </div>
    </Transition>

    <!-- Profil-Overlay: Zugriff auf Farbschemas und Zoomstufe (auch in der Demo nutzbar) -->
    <ProfileOverlay
      :is-open="isProfileOpen"
      :current-font-size="fontSize"
      :demo-mode="true"
      @close="isProfileOpen = false"
      @update:font-size="fontSize = $event"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import TopBar from '../components/TopBar.vue'
import LeftSidebar from '../components/LeftSidebar.vue'
import EditorArea from '../components/EditorArea.vue'
import RightPanel from '../components/RightPanel.vue'
import MdiIcon from '../components/MdiIcon.vue'
import ProfileOverlay from '../components/overlays/ProfileOverlay.vue'
import { mdiInformationOutline } from '@mdi/js'
import type { IdeFile, IdeConfig } from '../components/IdeWrapper.vue'
import type { TreeFolder } from '../services/projectService'
import { fileUid, uidPath } from '../services/projectService'
import { buildDemoTree } from '../services/demoProject'
import { installDemoApiLock, DEMO_LOCKED_EVENT } from '../services/demoMode'
import { useTheme, setActiveThemeMode } from '../composables/useThemeNew'

// ============================================================================
// Demo-Projekt laden (rein clientseitig, kein Backend)
// ============================================================================

const sidebarRoot = ref<TreeFolder | null>(null)
const fileStore = ref<Map<string, string>>(new Map())
const activeFile = ref('')

function loadDemoProject() {
  const { root, contents } = buildDemoTree()
  const firstProject = root.folders[0]
  const store = new Map<string, string>()
  // Key the store by file uid (projectId + path) to match the rest of the app.
  for (const [path, content] of Object.entries(contents)) {
    store.set(firstProject ? fileUid(firstProject.id, path) : path, content)
  }
  fileStore.value = store
  sidebarRoot.value = root
  // Erste Datei des Demo-Projekts aktivieren (per uid)
  const firstFile = firstProject?.files[0]
  activeFile.value = firstProject && firstFile ? fileUid(firstProject.id, firstFile.path) : ''
}

// ============================================================================
// UI-State (Layout, Theme, Zoom) — analog zu App.vue, aber ohne Persistenz
// ============================================================================

const runState = ref<'stopped' | 'running' | 'paused'>('stopped')
const speedVal = ref(100)
const panelWidth = ref(320)
const fontSize = ref(14)
const isDebugging = ref(false)
const showLeftSidebar = ref(true)
const showRightSidebar = ref(true)
const showBottomPanel = ref(true)
const theme = ref<'light' | 'dark'>('dark')
const editorAreaRef = ref<InstanceType<typeof EditorArea> | null>(null)

const isFullscreen = computed(() => !showLeftSidebar.value && !showRightSidebar.value)

const gridColsClass = computed(() => {
  if (showLeftSidebar.value && showRightSidebar.value) {
    return 'grid-cols-[280px_minmax(0,1fr)_var(--rp-width,320px)]'
  } else if (!showLeftSidebar.value && showRightSidebar.value) {
    return 'grid-cols-[0px_minmax(0,1fr)_var(--rp-width,320px)]'
  } else if (showLeftSidebar.value && !showRightSidebar.value) {
    return 'grid-cols-[280px_minmax(0,1fr)_0px]'
  } else {
    return 'grid-cols-[0px_minmax(0,1fr)_0px]'
  }
})

function toggleLeftSidebar() {
  showLeftSidebar.value = !showLeftSidebar.value
}
function toggleRightSidebar() {
  showRightSidebar.value = !showRightSidebar.value
}

function toggleBottomPanel() {
  showBottomPanel.value = !showBottomPanel.value
}
function toggleFullscreen() {
  if (isFullscreen.value) {
    showLeftSidebar.value = true
    showRightSidebar.value = true
  } else {
    showLeftSidebar.value = false
    showRightSidebar.value = false
  }
}
function zoomIn() {
  fontSize.value = Math.min(80, Math.round(fontSize.value * 1.1))
}
function zoomOut() {
  fontSize.value = Math.max(6, Math.round(fontSize.value * 0.9))
}

function toggleTheme() {
  const nextMode = theme.value === 'dark' ? 'light' : 'dark'
  setActiveThemeMode(nextMode)
  theme.value = nextMode
}

// ============================================================================
// IDE-Anbindung
// ============================================================================

const activeFilePath = computed(() => uidPath(activeFile.value))

const ideFiles = computed<IdeFile[]>(() => {
  const project = sidebarRoot.value?.folders[0]
  if (!project) return []
  return project.files.map((f) => ({
    name: f.path,
    content: fileStore.value.get(fileUid(project.id, f.path)) || '',
    type: 'java' as const,
  }))
})

const ideConfig = computed<IdeConfig>(() => ({
  speed: 'max',
  withFileList: false,
  withConsole: true,
  withErrorList: true,
  withPCode: false,
  withBottomPanel: showBottomPanel.value,
  hideEditor: false,
  hideStartPanel: false,
  libraries: [],
  enableFileAccess: false,
}))

// Änderungen bleiben nur in der Session (fileStore), es wird NICHTS gespeichert.
// Der Demo-Editor zeigt nur das eine Demo-Projekt, also gehört die Änderung dazu.
function onIdeChange(fileName: string, content: string) {
  const project = sidebarRoot.value?.folders[0]
  if (!project) return
  fileStore.value.set(fileUid(project.id, fileName), content)
}

function handleIdeAction(action: string, payload?: unknown) {
  if (editorAreaRef.value && typeof editorAreaRef.value.triggerIdeAction === 'function') {
    editorAreaRef.value.triggerIdeAction(action, payload)
  }
}

// Hinweis: Ein echter "Reset" entfällt bewusst — der Reset-Button zeigt im
// Demo-Modus nur den Hinweis, die Seite neu zu laden (siehe @reset im Template).

// ============================================================================
// Profil-Overlay (Zugriff auf Farbschemas / Zoomstufe — auch im Demo-Modus)
// ============================================================================

const isProfileOpen = ref(false)

// ============================================================================
// Read-only Hinweis für deaktivierte Aktionen — pro Button ein eigener Text
// ============================================================================

const noticeMessage = ref('')
let noticeTimer: ReturnType<typeof setTimeout> | null = null

/** Zeigt für einen im Demo-Modus deaktivierten Button eine spezifische Meldung an. */
function notifyDisabled(message: string) {
  noticeMessage.value = message
  if (noticeTimer) clearTimeout(noticeTimer)
  noticeTimer = setTimeout(() => {
    noticeMessage.value = ''
  }, 3000)
}

// ============================================================================
// Lifecycle
// ============================================================================

onMounted(async () => {
  // Alle Backend-/API-Calls im Demo-Modus zentral sperren (auch zukünftige
  // Features). Muss vor jeglicher Initialisierung passieren.
  installDemoApiLock()

  loadDemoProject()

  // Theme-System initialisieren (lädt themes.json, setzt CSS-Variablen).
  const { init } = useTheme()
  await init()

  // Aktuellen Modus aus dem gesetzten Attribut übernehmen (Slider-Position).
  const mode = document.documentElement.getAttribute('data-mode')
  theme.value = mode === 'light' ? 'light' : 'dark'

  // Wenn im Profil ein Farbschema/Modus gewählt wird, feuert setActiveThemeMode()
  // dieses Event — damit ziehen auch Monaco-Editor und Umschalter (Pill) mit.
  window.addEventListener('themeModeChanged', handleThemeModeChanged)

  // Gesperrte Backend-Calls (via installDemoApiLock) melden sich hier und zeigen
  // die Standard-Hinweis-Meldung im gleichen Notice-Banner an.
  window.addEventListener(DEMO_LOCKED_EVENT, handleDemoLocked)
})

function handleDemoLocked(event: Event) {
  const message = (event as CustomEvent).detail?.message
  if (message) notifyDisabled(message)
}

function handleThemeModeChanged(event: Event) {
  const mode = (event as CustomEvent).detail?.mode
  if (mode && mode !== theme.value) {
    theme.value = mode
  }
}

onUnmounted(() => {
  window.removeEventListener('themeModeChanged', handleThemeModeChanged)
  window.removeEventListener(DEMO_LOCKED_EVENT, handleDemoLocked)
  if (noticeTimer) clearTimeout(noticeTimer)
})
</script>
